import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import HeaderOrg from "../Headers/HeaderOrg";
import Loader from "../../Components/Loader/Loader";
import { serviceConsumer } from "../../network/ServiceConsumer";
import {
  getCheckIn,
  setCheckIn,
  setCheckInSet,
  getTabulationStatus,
} from "../../network/oireachtasEventDay";
import { getSchedule } from "../OireachtasPanel/Schedule/scheduleApi";
import { useMaterialSymbols, Msr } from "../OireachtasPanel/symbols";
import { useCan } from "../../device/DeviceContext";
import { clockTime } from "../ManageFeisGrade/RedirectFeis/Schedule/scheduleTiming";
import {
  buildStations,
  progressIndex,
  itemState,
  STATE_LABEL,
  firstOpenIndex,
  stepIndex,
  filterRoster,
  tally,
  tapState,
  notComingState,
  setText,
  changedSet,
} from "./checkInModel";
import "../OireachtasPanel/oireachtas.css";
import "../OireachtasPanel/organizerOireachtas.css";
import "./oireachtasCheckIn.css";

// Check-in (tabulation-and-results.md §4; live stage display Phase 2). Built
// for the side-stage tablet (Fire HD 8 / iPad, Devin 2026-10-07) and laid out
// by the stage schedule, because check-in is what feeds live schedule
// information: pick the day and stage, work down its running order. Only
// CHECKED-IN dancers count as having danced for recalls, placements and
// qualifiers. Every dancer due must be marked Checked in or No show (Devin
// 2026-10-07): Next waits until they are, and publishing refuses an unmarked
// dancer. A recall round lists only the dancers in the published recall, and
// nobody until it is published. Every tap saves on its own (batched for a moment), with the save
// state always visible. Check-in is a reference only (Devin 2026-10-22):
// publishing never locks it. On a recall round with a set dance choice,
// each dancer's set + speed shows on the tile: checking the dancer in
// confirms it, and a wrong set is changed (then the dancer checked in) — Devin
// 2026-10-07; the announcer reads it with the number. A
// round-dance choice round (Under 7 recall, Adult round 2) shows each
// dancer's registered dance.

const SAVE_DELAY_MS = 400;

const OireachtasCheckIn = () => {
  useMaterialSymbols();
  const navigate = useNavigate();
  const location = useLocation();
  const eventId = new URLSearchParams(location.search).get("id");

  const [event, setEvent] = useState(null);
  const [stations, setStations] = useState([]);
  const [progress, setProgress] = useState(new Map());
  const [dayPick, setDayPick] = useState(null);
  const [stationKey, setStationKey] = useState("");
  const [itemIndex, setItemIndex] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [data, setData] = useState(null); // getCheckIn response for the round in dataKey
  const [dataKey, setDataKey] = useState(""); // the competition-round `data` belongs to
  const [rosterError, setRosterError] = useState("");
  const [rosterBusy, setRosterBusy] = useState(false);
  const [marks, setMarks] = useState({}); // ref -> "checked_in" | "no_show" (shown round)
  const [filter, setFilter] = useState("all");
  const [find, setFind] = useState("");
  const [sets, setSets] = useState({}); // ref -> set shown (set rounds only)
  const [editing, setEditing] = useState(null); // { row, choiceKey, speed, custom }
  const [leaveError, setLeaveError] = useState("");
  const [, setTick] = useState(0); // re-render after save bookkeeping in refs
  const bump = () => setTick((n) => n + 1);

  // The competition-round selected, and the latest roster request: only that
  // request's response is shown (code review 73f1a43d R1-F3).
  const current = useRef("");
  const reqSeq = useRef(0);
  const dataKeyRef = useRef(""); // dataKey, for callbacks
  const loadRef = useRef(null); // loadRoster, for its own delayed re-read
  const marksRef = useRef({});
  // Saving (code review 73f1a43d R1-F2): unsaved taps queue PER ROUND and
  // survive navigation; a failed round keeps its taps and its error until a
  // save succeeds. queues: key -> { ref -> { refModel, state } } ("clear" =
  // unmark); failed: key -> message.
  const queues = useRef(new Map());
  const failed = useRef(new Map());
  const timer = useRef(null);
  const flushing = useRef(null);
  // whether this device's job still checks dancers in (card 540f21a4): the
  // job is re-read every minute, and the save queue runs later than the tap
  const canMarkRef = useRef(true);
  const flushAgain = useRef(false);
  const savedOnce = useRef(false);
  // Writes per round, so a roster read that overlapped a write is re-read
  // rather than shown (code review 73f1a43d R2-F2): a counter bumped by every
  // write start/end, and the rounds with a write in flight.
  const mutSeq = useRef(new Map());
  const writing = useRef(new Map());
  const touch = (key) => mutSeq.current.set(key, (mutSeq.current.get(key) || 0) + 1);
  const writeStart = (key) => {
    touch(key);
    writing.current.set(key, (writing.current.get(key) || 0) + 1);
  };
  const writeEnd = (key) => {
    touch(key);
    const n = (writing.current.get(key) || 1) - 1;
    if (n > 0) writing.current.set(key, n);
    else writing.current.delete(key);
  };
  // a request the server refused for good (4xx): not retried
  const isRefusal = (e) => Number(e?.statuscode) >= 400 && Number(e?.statuscode) < 500;
  // Set changes in flight / failed, keyed "round|ref" (code review R1-F4..F6)
  const setOps = useRef(new Map());
  const failedSets = useRef(new Map());

  const station = stations.find((s) => s.key === stationKey) || null;
  const items = station ? station.items : [];
  const item = itemIndex >= 0 ? items[itemIndex] : null;

  // ---- load ------------------------------------------------------------------

  const refreshProgress = useCallback(async () => {
    try {
      setProgress(progressIndex(await getTabulationStatus(eventId)));
    } catch (e) {
      // progress is a convenience: the roster still loads without it
    }
  }, [eventId]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [eventRes, sched, st] = await Promise.all([
          serviceConsumer("GET", `${process.env.REACT_APP_BASE_URL}/oireachtas/${eventId}`),
          getSchedule(eventId),
          getTabulationStatus(eventId),
        ]);
        setEvent(eventRes.event);
        const built = buildStations(sched);
        const prog = progressIndex(st);
        setStations(built.stations);
        setProgress(prog);
        const first = built.stations.find((s) => s.items.some((it) => it.kind === "round")) || null;
        if (first) {
          setDayPick(first.day);
          setStationKey(first.key);
          setItemIndex(firstOpenIndex(first.items, prog));
        }
      } catch (e) {
        setLoadError(e?.messages || "Could not load check-in");
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId]);

  // ---- the selected competition-round ----------------------------------------

  // The server's marks with this round's unsaved taps on top.
  const withQueued = (key, serverMarks) => {
    const m = { ...serverMarks };
    for (const [ref, v] of Object.entries(queues.current.get(key) || {})) {
      if (v.state === "clear") delete m[ref];
      else m[ref] = v.state;
    }
    return m;
  };

  // quiet = a background refresh (no busy state). A read that overlapped a
  // write to this round is read again once the writes settle.
  const loadRoster = useCallback(
    async (compId, round, key, quiet = false) => {
      current.current = key;
      const id = ++reqSeq.current;
      if (!quiet) {
        setRosterBusy(true);
        setRosterError("");
      }
      try {
        let res;
        let overlapped = false;
        for (let attempt = 0; ; attempt++) {
          const seqAt = mutSeq.current.get(key) || 0;
          const busyAt = writing.current.has(key);
          res = await getCheckIn(eventId, compId, round);
          if (id !== reqSeq.current) return;
          overlapped = busyAt || (mutSeq.current.get(key) || 0) !== seqAt;
          if (!overlapped || attempt >= 2) break;
          if (flushing.current) await flushing.current;
          await Promise.all([...setOps.current.values()].filter((op) => op.key === key).map((op) => op.done));
          if (id !== reqSeq.current) return;
        }
        if (overlapped) {
          // still racing writes: never show a read that may predate them
          // (code review 73f1a43d R2-F2, round 3) — look again shortly
          setTimeout(() => {
            if (current.current === key && loadRef.current) loadRef.current(compId, round, key, true);
          }, 1500);
          if (dataKeyRef.current === key) return; // keep what's shown
        }
        // a recall that appeared (or any quiet re-read): refresh the running
        // order's progress too (code review 73f1a43d R3-F3)
        if (quiet || res.recallPublished === true) refreshProgress();
        const m = {};
        const st = {};
        res.roster.forEach((row) => {
          if (row.state) m[row.ref] = row.state;
          if (row.set) st[row.ref] = row.set;
        });
        const shown = withQueued(key, m);
        marksRef.current = shown;
        dataKeyRef.current = key;
        setData(res);
        setDataKey(key);
        setMarks(shown);
        setSets(st);
      } catch (e) {
        if (id !== reqSeq.current || quiet) return;
        setData(null);
        dataKeyRef.current = key;
        setDataKey(key);
        marksRef.current = {};
        setMarks({});
        setSets({});
        setRosterError(e?.messages || "Could not load this round");
      } finally {
        // whichever request is the latest ends the busy state, quiet or not
        // (code review 73f1a43d R3-F2)
        if (id === reqSeq.current) setRosterBusy(false);
      }
    },
    [eventId] // eslint-disable-line react-hooks/exhaustive-deps
  );
  loadRef.current = loadRoster;

  // ---- saving: every tap saves, batched for a moment ---------------------------

  // Send every round's queued taps, one request per round, through ONE worker
  // (code review 73f1a43d R2-F1): a call while it runs asks it to go round
  // again rather than starting a second, overlapping sender. A sent tap
  // leaves its queue only if no newer tap for that dancer arrived meanwhile.
  // Marking unticked meanwhile (card 540f21a4): nothing left can be sent;
  // drop it and show what the server has (MC review c87cf2d7 R5-F1/R6-F2)
  const discardUnsendable = () => {
    queues.current.clear();
    failedSets.current.clear();
    failed.current.clear();
    const key = current.current;
    if (key) {
      const [compId, roundText] = key.split(":");
      loadRoster(compId, Number(roundText), key, true);
    }
    bump();
  };

  const sendQueues = async () => {
      for (const [key, q] of [...queues.current.entries()]) {
        // checked before every request, not only when the worker starts
        if (!canMarkRef.current) {
          discardUnsendable();
          return;
        }
        const entries = Object.entries(q);
        if (entries.length === 0) {
          queues.current.delete(key);
          continue;
        }
        const [compId, roundText] = key.split(":");
        const payload = entries.map(([ref, v]) => ({ ref, refModel: v.refModel, state: v.state }));
        writeStart(key);
        try {
          const res = await setCheckIn(eventId, compId, Number(roundText), payload);
          const cur = queues.current.get(key) || {};
          for (const [ref, v] of entries) if (cur[ref] === v) delete cur[ref];
          if (Object.keys(cur).length === 0) queues.current.delete(key);
          failed.current.delete(key);
          savedOnce.current = true;
          setProgress((prev) => {
            const next = new Map(prev);
            const p = next.get(key);
            if (p) next.set(key, { ...p, taken: res.marked > 0, checkedIn: res.danced, notComing: res.noShow });
            return next;
          });
        } catch (e) {
          if (isRefusal(e)) {
            // refused for good (locked, recall changed, not allowed): drop the
            // taps, say why once, show the server's state
            queues.current.delete(key);
            failed.current.set(key, e?.messages || "This round can't be changed");
            if (current.current === key) loadRoster(compId, Number(roundText), key, true);
          } else {
            failed.current.set(key, e?.messages || "Check the connection");
          }
        } finally {
          writeEnd(key);
        }
      }
  };
  const flush = useCallback(() => {
    clearTimeout(timer.current);
    // unticked meanwhile: what is left can't be sent (MC review c87cf2d7 R5-F1)
    if (!canMarkRef.current) {
      discardUnsendable();
      return Promise.resolve();
    }
    if (flushing.current) {
      flushAgain.current = true;
      return flushing.current;
    }
    const run = (async () => {
      do {
        flushAgain.current = false;
        await sendQueues();
      } while (flushAgain.current);
    })().finally(() => {
      flushing.current = null;
      bump();
    });
    flushing.current = run;
    bump();
    return run;
  }, [eventId, loadRoster]); // eslint-disable-line react-hooks/exhaustive-deps

  const queue = (row, state, key = current.current) => {
    touch(key);
    const q = queues.current.get(key) || {};
    q[row.ref] = { refModel: row.refModel, state: state || "clear" };
    queues.current.set(key, q);
    if (key === current.current) {
      const next = { ...marksRef.current };
      if (state) next[row.ref] = state;
      else delete next[row.ref];
      marksRef.current = next;
      setMarks(next);
    }
    bump();
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, SAVE_DELAY_MS);
  };

  // A set change (code review R1-F4..F6): its tile is locked until it ends;
  // success checks the dancer in for THAT round even if the screen moved on;
  // failure puts the set back and keeps the change for Retry.
  const runSetOp = async (op) => {
    if (!canMarkRef.current) return;
    const id = `${op.key}|${op.row.ref}`;
    let finish;
    const tracked = { ...op, done: new Promise((r) => { finish = r; }) };
    setOps.current.set(id, tracked);
    failedSets.current.delete(id);
    writeStart(op.key);
    if (op.key === current.current) setSets((prev) => ({ ...prev, [op.row.ref]: op.next }));
    bump();
    try {
      await setCheckInSet(eventId, op.compId, {
        round: op.round,
        ref: op.row.ref,
        danceChoice: op.next.danceChoice,
        speed: op.next.speed,
      });
      savedOnce.current = true;
      // a roster read during the request may have shown the old set
      if (op.key === current.current) setSets((prev) => ({ ...prev, [op.row.ref]: op.next }));
      queue(op.row, "checked_in", op.key);
    } catch (e) {
      if (op.key === current.current) setSets((prev) => ({ ...prev, [op.row.ref]: op.before }));
      if (isRefusal(e)) {
        // refused for good (invalid, locked, recall changed): say why once,
        // nothing to retry (code review 73f1a43d R2-F3)
        failed.current.set(op.key, `${e?.messages || "Set change refused"} (set for #${op.row.number})`);
        if (op.key === current.current) loadRoster(op.compId, op.round, op.key, true);
      } else {
        failedSets.current.set(id, { ...op, message: e?.messages || "Check the connection" });
      }
    } finally {
      writeEnd(op.key);
      setOps.current.delete(id);
      finish();
      bump();
    }
  };

  const retry = async () => {
    if (!canMarkRef.current) {
      // nothing can be sent any more: Dismiss
      await flush();
      return;
    }
    const ops = [...failedSets.current.values()];
    failedSets.current.clear();
    for (const key of [...failed.current.keys()]) {
      if (!queues.current.has(key)) failed.current.delete(key); // a locked round: nothing left to send
    }
    bump();
    for (const op of ops) await runSetOp(op);
    await flush();
  };

  const openChange = (row) => {
    const set = sets[row.ref] || {};
    const choices = (data?.setChoices && data.setChoices[row.setGate]) || [];
    const choice = choices.find((c) => c.key === set.danceChoice) || choices[0] || null;
    const listed = choice && choice.speeds.includes(String(set.speed));
    setEditing({ row, choiceKey: choice ? choice.key : "", speed: listed ? String(set.speed) : "", custom: listed ? "" : String(set.speed || "") });
  };
  const editChoices = editing ? (data?.setChoices && data.setChoices[editing.row.setGate]) || [] : [];
  const editChoice = editing ? editChoices.find((c) => c.key === editing.choiceKey) || null : null;
  const editTraditional = !!editChoice && editChoice.speeds.length === 1 && editChoice.speeds[0] === "traditional";
  const editSpeed = editing ? (editTraditional ? "traditional" : editing.custom.trim() || editing.speed) : "";
  const saveChange = () => {
    // the job is re-read every minute (MC review c87cf2d7 R4-F1)
    if (!canMark) {
      setEditing(null);
      return;
    }
    if (!editChoice || !editSpeed || !item) return;
    const { row } = editing;
    setEditing(null);
    runSetOp({
      key: item.key,
      compId: item.compId,
      round: item.round,
      row,
      before: sets[row.ref],
      next: changedSet(sets[row.ref], editChoice, editSpeed),
    });
  };

  // warn before leaving with taps not yet saved
  useEffect(() => {
    const warn = (e) => {
      if (queues.current.size || flushing.current || setOps.current.size || failedSets.current.size) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);

  useEffect(() => {
    if (!item || item.kind !== "round") return;
    flush(); // send what's queued; anything that fails stays queued for its round
    setFilter("all");
    setFind("");
    setLeaveError("");
    loadRoster(item.compId, item.round, item.key);
  }, [item?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // A recall round waiting for its recall refreshes itself (code review R1-F7).
  const waitingRecall = !!(data && data.recallPublished === false && item && dataKey === item.key);
  useEffect(() => {
    if (!waitingRecall) return undefined;
    const t = setInterval(() => loadRoster(item.compId, item.round, item.key, true), 20000);
    return () => clearInterval(t);
  }, [waitingRecall, item?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickStation = (key) => {
    const s = stations.find((x) => x.key === key);
    setStationKey(key);
    setItemIndex(s ? firstOpenIndex(s.items, progress) : -1);
    refreshProgress();
  };
  const pickItem = (i) => {
    // tapping the round already shown reloads it
    if (i === itemIndex && item && item.kind === "round") loadRoster(item.compId, item.round, item.key);
    setItemIndex(i);
    refreshProgress();
  };
  const prevIndex = item ? stepIndex(items, itemIndex, -1) : -1;
  const nextIndex = item ? stepIndex(items, itemIndex, 1) : -1;

  const leave = async () => {
    await flush();
    // a refusal notice doesn't hold the screen; unsaved or failed work does
    if (queues.current.size || failedSets.current.size || setOps.current.size || flushing.current) {
      setLeaveError("Some check-ins aren't saved yet — tap Retry, then leave.");
      return;
    }
    navigate(`/oireachtas/manage?id=${eventId}`);
  };

  // ---- derived ------------------------------------------------------------------

  // Tiles act only on the round they were loaded for (code review R1-F1).
  const ready = !!(item && dataKey === item.key && !rosterBusy);
  // an event-day device whose job only sees check-in (card 540f21a4)
  const canMark = useCan()("ox.checkin.mark");
  canMarkRef.current = canMark;
  // an open set editor closes when Check dancers in is unticked
  useEffect(() => {
    if (!canMark) setEditing(null);
  }, [canMark]);
  const roster = useMemo(() => (data && data.roster) || [], [data]);
  const counts = tally(roster, marks);
  const shown = filterRoster(roster, marks, filter, find);
  const setRound = roster.some((r) => sets[r.ref]);
  // publishing no longer locks check-in (Devin 2026-10-22: check-in has no
  // effect on results); a lock left by an older publication is refused by
  // the server with its own message
  // An older server (no `taken`) still counts unmarked dancers as danced.
  const legacyServer = !!data && typeof data.taken !== "boolean";
  const showsSetDance = !!data && (typeof data.showsSetDance === "boolean" ? data.showsSetDance : !!data.isRecallRound);
  const showsRoundDance = !!(data && data.showsRoundDance);
  const isTeam = roster[0]?.refModel === "oireachtasTeam";
  const days = [...new Set(stations.filter((s) => s.day).map((s) => s.day))];
  const activeDay = dayPick || days[0] || null;
  const dayStations = stations.filter((s) => s.day === activeDay);
  const unscheduled = stations.find((s) => !s.day);
  const busyRef = (ref) => !!item && setOps.current.has(`${item.key}|${ref}`);

  // Save status across every round (a failure elsewhere names its round).
  const roundName = (key) => {
    for (const s of stations) {
      const it = s.items.find((x) => x.kind === "round" && x.key === key);
      if (it) return `${it.comp?.code} ${it.card?.label || `round ${it.round}`}`;
    }
    return "another round";
  };
  const firstFailure = (() => {
    const setFail = [...failedSets.current.values()][0];
    if (setFail) return { key: setFail.key, message: `${setFail.message} (set for #${setFail.row.number})` };
    const [key, message] = [...failed.current.entries()][0] || [];
    return key ? { key, message } : null;
  })();
  const busySaving = !!(flushing.current || setOps.current.size || queues.current.size);
  // something to send again, or only refusal notices to acknowledge
  const retriable =
    canMark && (failedSets.current.size > 0 || [...failed.current.keys()].some((k) => queues.current.has(k)));
  const saveLine = firstFailure ? (
    <span className="ci-save error" role="alert">
      <Msr>error</Msr> Not saved — {firstFailure.message}
      {item && firstFailure.key !== item.key ? ` · ${roundName(firstFailure.key)}` : ""}
      <button type="button" className="ci-link" onClick={retry}>{retriable ? "Retry" : "Dismiss"}</button>
    </span>
  ) : busySaving ? (
    <span className="ci-save saving"><Msr>sync</Msr> Saving…</span>
  ) : savedOnce.current ? (
    <span className="ci-save saved"><Msr>check_circle</Msr> Saved</span>
  ) : (
    <span className="ci-save"><Msr>touch_app</Msr> Tap a dancer to check them in</span>
  );

  const regLine = (row) => {
    if (showsRoundDance) return `${row.roundDanceName || "—"}${row.roundDanceSpeed ? ` · ${row.roundDanceSpeed}` : ""}`;
    // the set has its own line with Confirm / Change; an older server shows the raw registration
    if (showsSetDance && !sets[row.ref]) return `${row.danceChoice || "—"}${row.speed ? ` · ${row.speed}` : ""}`;
    return "";
  };

  // ---- render -------------------------------------------------------------------

  return (
    <>
      {loading && <Loader />}
      <HeaderOrg />
      <div className="oir op-page ci-page">
        <div className="wrap">
          <button
            type="button"
            className="op-back"
            onClick={leave}
          >
            <Msr>arrow_back</Msr> {event?.name || "Event"}
          </button>
          {leaveError && <div className="op-error" role="alert">{leaveError}</div>}
          <div className="ph">
            <div>
              <h1>Check-in</h1>
              <div className="sub">
                <span>Mark every dancer Checked in or No show. Check-in is optional for tabulation: a dancer a judge scores counts as danced, and a No show is left off the judges' sheets.</span>
              </div>
            </div>
          </div>

          {loadError && <div className="op-error">{loadError}</div>}

          {!loading && !loadError && (
            <>
              {/* day + stage */}
              <div className="ci-pickers">
                {days.length > 1 && (
                  <div className="ci-pills" role="tablist" aria-label="Day">
                    {days.map((d) => {
                      const s = stations.find((x) => x.day === d);
                      return (
                        <button
                          key={d}
                          type="button"
                          role="tab"
                          aria-selected={d === activeDay && !!station?.day}
                          className={`op-chip${d === activeDay && station?.day ? " on" : ""}`}
                          onClick={() => {
                            setDayPick(d);
                            const first = stations.find((x) => x.day === d);
                            if (first) pickStation(first.key);
                          }}
                        >
                          <Msr>calendar_today</Msr> {s?.dayLabel || d}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="ci-pills" role="tablist" aria-label="Stage">
                  {dayStations.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      role="tab"
                      aria-selected={s.key === stationKey}
                      className={`op-chip${s.key === stationKey ? " on" : ""}`}
                      onClick={() => pickStation(s.key)}
                    >
                      {s.name}
                    </button>
                  ))}
                  {unscheduled && (
                    <button
                      type="button"
                      role="tab"
                      aria-selected={unscheduled.key === stationKey}
                      className={`op-chip${unscheduled.key === stationKey ? " on" : ""}`}
                      onClick={() => pickStation(unscheduled.key)}
                    >
                      {unscheduled.name}
                    </button>
                  )}
                </div>
                {stations.length === 1 && unscheduled && (
                  <div className="op-note">No stage schedule yet — every competition-round is listed in syllabus order.</div>
                )}
              </div>

              <div className="ci-layout">
                {/* the stage's running order */}
                <nav className="ci-order" aria-label="Running order">
                  {items.map((it, i) => {
                    if (it.kind === "note") {
                      return (
                        <div key={it.key} className="ci-note">
                          {it.start != null && <span className="ci-time">{clockTime(it.start)}</span>}
                          <span>{it.label}</span>
                        </div>
                      );
                    }
                    const p = progress.get(it.key);
                    const state = itemState(p, it.round);
                    return (
                      <button
                        key={it.key}
                        type="button"
                        className={`ci-oitem ${state}${i === itemIndex ? " on" : ""}`}
                        aria-current={i === itemIndex ? "step" : undefined}
                        onClick={() => pickItem(i)}
                      >
                        <span className="ci-time">{it.start != null ? clockTime(it.start) : ""}</span>
                        <span className="ci-otext">
                          <span className="ci-oname">{it.comp?.name || it.comp?.code}</span>
                          <span className="ci-olabel">
                            {it.comp?.code} · {it.card?.label || `Round ${it.round}`}
                          </span>
                        </span>
                        <span className={`ci-ostate ${state}`}>
                          {state === "partial" || state === "done" ? `${p.checkedIn}/${p.count}` : STATE_LABEL[state]}
                        </span>
                      </button>
                    );
                  })}
                  {items.length === 0 && <div className="op-empty">Nothing on this stage yet</div>}
                </nav>

                {/* the selected competition-round */}
                <section className="ci-main">
                  {item && item.kind === "round" ? (
                    <>
                      <div className="ci-head">
                        <button
                          type="button"
                          className="ci-step"
                          disabled={prevIndex < 0}
                          aria-label="Previous in running order"
                          onClick={() => pickItem(prevIndex)}
                        >
                          <Msr>chevron_left</Msr>
                        </button>
                        <div className="ci-title">
                          <div className="ci-name">
                            {item.comp?.name || item.comp?.code}
                            {item.start != null && <span className="ci-at">{clockTime(item.start)}</span>}
                          </div>
                          <div className="ci-code">
                            {item.comp?.code} · {item.card?.label || `Round ${item.round}`}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="ci-step"
                          disabled={nextIndex < 0}
                          aria-label="Next in running order"
                          onClick={() => pickItem(nextIndex)}
                        >
                          <Msr>chevron_right</Msr>
                        </button>
                      </div>

                      {legacyServer && (
                        <div className="ci-banner" role="note">
                          <Msr>info</Msr>
                          <span>
                            This server still counts unmarked dancers as having danced — tick <b>No show</b>{" "}
                            for every dancer who did not dance.
                          </span>
                        </div>
                      )}
                      {!canMark && (
                        <div className="ci-banner" role="note">
                          <Msr>visibility</Msr>
                          <span>View only — this device's job doesn't check dancers in.</span>
                        </div>
                      )}
                      {rosterError && <div className="op-error">{rosterError}</div>}

                      {waitingRecall && (
                        <div className="ci-waiting">
                          <Msr>hourglass_top</Msr>
                          <div>
                            <b>The recall for {item.comp?.code} isn't published yet.</b>
                            <div>The recalled dancers appear here as soon as it is.</div>
                          </div>
                        </div>
                      )}

                      {data && data.recallPublished !== false && (
                        <>
                          <div className="ci-filters">
                            {[
                              ["all", "All", roster.length],
                              ["blank", "To mark", counts.blank],
                              ["checked_in", "Checked in", counts.checkedIn],
                              ["no_show", "No show", counts.notComing],
                            ].map(([key, label, n]) => (
                              <button
                                key={key}
                                type="button"
                                aria-pressed={filter === key}
                                className={`op-chip ci-count ${key}${filter === key ? " on" : ""}`}
                                onClick={() => setFilter(key)}
                              >
                                <b>{n}</b> {label}
                              </button>
                            ))}
                            <label className="op-search ci-find">
                              <Msr>search</Msr>
                              <input
                                inputMode="numeric"
                                placeholder="Find #"
                                aria-label="Find a competitor number"
                                value={find}
                                onChange={(e) => setFind(e.target.value)}
                              />
                            </label>
                          </div>

                          <div className={`ci-grid${setRound ? " with-sets" : ""}${rosterBusy ? " busy" : ""}`}>
                            {shown.map((row) => {
                              const state = marks[row.ref] || "";
                              const reg = regLine(row);
                              const set = sets[row.ref];
                              return (
                                <div key={row.ref} className={`ci-tile ${state}${set ? " has-set" : ""}`}>
                                  <div className="ci-tile-main">
                                  <button
                                    type="button"
                                    className="ci-tap"
                                    disabled={!canMark || !ready || busyRef(row.ref)}
                                    aria-pressed={state === "checked_in"}
                                    aria-label={`${row.number} ${row.label || row.schoolName}: ${
                                      state === "checked_in" ? "checked in" : state === "no_show" ? "no show" : "not marked"
                                    }`}
                                    onClick={() => queue(row, tapState(marks[row.ref]))}
                                  >
                                    <span className="ci-num">{row.number}</span>
                                    <span className="ci-who">
                                      <span className="ci-dancer">{row.label || row.schoolName}</span>
                                      {/* a team reads "Team A" with its school (an unlettered team's label is the school) */}
                                      {row.schoolName && (!isTeam || row.label !== row.schoolName) && <span className="ci-school">{row.schoolName}</span>}
                                      {reg && <span className="ci-reg">{reg}</span>}
                                    </span>
                                    <span className="ci-mark" aria-hidden="true">
                                      <Msr>
                                        {state === "checked_in"
                                          ? "check_circle"
                                          : state === "no_show"
                                          ? "do_not_disturb_on"
                                          : "radio_button_unchecked"}
                                      </Msr>
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="ci-nc"
                                    disabled={!canMark || !ready || busyRef(row.ref)}
                                    aria-pressed={state === "no_show"}
                                    onClick={() => queue(row, notComingState(marks[row.ref]))}
                                  >
                                    No show
                                  </button>
                                  </div>
                                  {set && (
                                    <div className="ci-set">
                                      <span className="ci-set-text">
                                        <Msr>music_note</Msr>
                                        <span>
                                          <b>{setText(set)}</b>
                                          {set.changed && (
                                            <span className="ci-set-was">
                                              Changed — registered {setText(set.registered)}
                                            </span>
                                          )}
                                        </span>
                                      </span>
                                      {canMark && (
                                        <button
                                          type="button"
                                          className="ci-set-btn"
                                          disabled={!ready || busyRef(row.ref)}
                                          aria-label={`Change the set for ${row.number}`}
                                          onClick={() => openChange(row)}
                                        >
                                          Change
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            {shown.length === 0 && <div className="op-empty">No dancers match</div>}
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="op-empty">Pick a competition-round from the running order</div>
                  )}
                </section>
              </div>
            </>
          )}
        </div>

        {editing && (
          <div className="ci-scrim" role="presentation" onClick={() => setEditing(null)}>
            <div
              className="ci-sheet"
              role="dialog"
              aria-modal="true"
              aria-label={`Change the set for ${editing.row.number}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ci-sheet-h">
                <span className="ci-num">{editing.row.number}</span>
                <span className="ci-who">
                  <span className="ci-dancer">{editing.row.label}</span>
                  <span className="ci-school">Registered: {setText(sets[editing.row.ref]?.registered)}</span>
                </span>
              </div>
              <label className="ci-field">
                <span>Set dance</span>
                <select
                  value={editing.choiceKey}
                  onChange={(e) => setEditing({ ...editing, choiceKey: e.target.value, speed: "", custom: "" })}
                >
                  {editChoices.map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              {editTraditional ? (
                <div className="ci-field"><span>Speed</span><b>Traditional speed</b></div>
              ) : (
                <div className="ci-field-row">
                  <label className="ci-field">
                    <span>Speed</span>
                    <select value={editing.speed} onChange={(e) => setEditing({ ...editing, speed: e.target.value, custom: "" })}>
                      <option value="">Pick…</option>
                      {(editChoice?.speeds || []).map((sp) => (
                        <option key={sp} value={sp}>
                          {sp}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="ci-field">
                    <span>Other speed</span>
                    <input
                      inputMode="numeric"
                      placeholder="e.g. 98"
                      value={editing.custom}
                      onChange={(e) => setEditing({ ...editing, custom: e.target.value.replace(/\D/g, ""), speed: "" })}
                    />
                  </label>
                </div>
              )}
              <div className="ci-sheet-a">
                <button type="button" className="ab-back" onClick={() => setEditing(null)}>
                  Cancel
                </button>
                <button type="button" className="ab-next" disabled={!editChoice || !editSpeed} onClick={saveChange}>
                  <Msr>check</Msr> Save and check in
                </button>
              </div>
            </div>
          </div>
        )}

        {item && item.kind === "round" && data && (
          <div className="actionbar">
            <div className="actionbar-in ci-bar">
              <div className="ci-bar-status">
                {saveLine}
                <span className="ci-bar-counts">
                  {counts.checkedIn} checked in · {counts.notComing} no show · {counts.blank} to mark
                </span>
              </div>
              {/* every dancer must be marked before moving on (the running
                  order and arrows still move freely, to go back and fix) */}
              <button
                type="button"
                className="ab-next"
                disabled={nextIndex < 0 || !ready || (canMark && counts.blank > 0)}
                onClick={() => pickItem(nextIndex)}
              >
                {counts.blank > 0 ? `${counts.blank} to mark` : "Next"} <Msr>arrow_forward</Msr>
              </button>
            </div>
          </div>
        )}
      </div>
      <ToastContainer />
    </>
  );
};

export default OireachtasCheckIn;
